import { createElement, useEffect, type ReactNode } from 'react';
import {
  AUDIENCE_FIELD_DEFINITIONS,
  BRAND_FIELD_DEFINITIONS,
  type BrandListFieldKey,
  type ValuePhilosophy,
} from '@mg/shared';
import { brandFieldDescribedBy, textLimitError } from '../../brand/fields/fieldParts';
import type { TagTone } from '../../brand/fields/TagListInput';
import { useBrandDraft } from '../draft/BrandDraftContext';
import { audienceFieldId, sameValue, wizardFieldId, type BrandDraft, type FieldValue } from '../draft/brandDraft';
import type { WizardFieldRef } from '../steps';
import { GUIDELINES_FIELD_LABEL } from '../wizardCopy';
import { ChoiceChanges, GuidelineChanges, TagChanges, TextChanges } from './FieldChanges';
import { textChangesToShow } from './revisitChanges';
import { useRevisit, type RevisitShowing } from './RevisitProvider';

export interface RevisitFieldState {
  /** Show changes is on, or a re-read is running: the field cannot be edited (spec:134). */
  readOnly: boolean;
  /** Drawn in place of the field's control while Show changes is on and the field has changed. */
  changes: ReactNode | null;
}

const EDITABLE: RevisitFieldState = { readOnly: false, changes: null };
const WAITING: RevisitFieldState = { readOnly: true, changes: null };

/** Each Vocabulary list's colour (spec:263), as the wizard's field draws it. */
const TAG_TONES: Record<Exclude<BrandListFieldKey, 'writingGuidelines'>, TagTone> = {
  keywords: 'teal',
  alwaysSay: 'always',
  neverSay: 'never',
  socialHashtags: 'teal',
  catchphrases: 'teal',
};

const textOf = (value: FieldValue): string => (typeof value === 'string' ? value : '');
const listOf = (value: FieldValue): string[] => (Array.isArray(value) ? value : []);
const choiceOf = (value: FieldValue): ValuePhilosophy | null => (typeof value === 'string' && value !== '' ? (value as ValuePhilosophy) : null);

/**
 * One wizard field in a revisit (spec:131-139; Release 4, lane W7):
 * - while a re-read runs: read-only (its proposal is on the way);
 * - while Show changes is on: read-only, and when its value differs from the saved one, its changes in
 *   place of the control (text word by word, lists item by item, Value philosophy old and new option);
 * - otherwise (Show changes off, no re-read, first-time setup): editable as it is.
 */
export function useRevisitField(field: WizardFieldRef): RevisitFieldState {
  const revisit = useRevisit();
  const { state } = useBrandDraft();

  // A field of steps 2-8 is on the screen: a start error from step 1 is out of date.
  const fieldShown = revisit?.fieldShown;
  useEffect(() => {
    fieldShown?.();
  }, [fieldShown]);

  if (!revisit) return EDITABLE;
  if (revisit.locked) return WAITING;
  if (!revisit.showing) return EDITABLE;
  return { readOnly: true, changes: changesOf(field, state, revisit.showing) };
}

/** A field's Show changes, or null while its value is the saved one (its control shows, read-only). */
function changesOf(field: WizardFieldRef, draft: BrandDraft, showing: RevisitShowing): ReactNode | null {
  const { view } = showing;

  if (field.kind === 'field') {
    const key = field.key;
    const def = BRAND_FIELD_DEFINITIONS[key];
    const f = draft.fields[key];
    if (sameValue(f.value, f.saved)) return null;
    const id = wizardFieldId(key);
    const isText = def.kind === 'text' || def.kind === 'textarea';
    const describedBy = brandFieldDescribedBy(id, {
      // The guidelines' step card already says what the rules are for, so that field shows no hint.
      hint: def.kind === 'guidelines' ? null : def.hint,
      sources: f.sources,
      aiSuggested: f.aiSuggested,
      error: isText ? textLimitError(textOf(f.value), def.maxLength) : null,
    });
    switch (def.kind) {
      case 'text':
      case 'textarea':
        return createElement(TextChanges, {
          id,
          label: def.label,
          text: textOf(f.value),
          multiline: def.kind === 'textarea',
          describedBy,
          changes: textChangesToShow({
            saved: f.saved,
            value: f.value,
            proposed: view.proposal.fields[key],
            baseline: view.baseline.fields[key] as FieldValue,
          }),
        });
      case 'tags':
        return createElement(TagChanges, {
          id,
          label: def.label,
          saved: listOf(f.saved),
          current: listOf(f.value),
          tone: TAG_TONES[key as keyof typeof TAG_TONES] ?? 'teal',
          describedBy,
        });
      case 'guidelines':
        return createElement(GuidelineChanges, {
          id,
          label: GUIDELINES_FIELD_LABEL,
          saved: listOf(f.saved),
          current: listOf(f.value),
          describedBy,
        });
      default:
        return createElement(ChoiceChanges, {
          id,
          label: def.label,
          saved: choiceOf(f.saved),
          current: choiceOf(f.value),
          describedBy,
        });
    }
  }

  const card = draft.audiences.find((c) => c.key === field.card);
  if (!card) return null;
  const f = card.fields[field.key];
  if (sameValue(f.value, f.saved)) return null;
  const def = AUDIENCE_FIELD_DEFINITIONS[field.key];
  const id = audienceFieldId(card.key, field.key);
  // The proposed audience this card shows: the one the re-read put in it, or the saved one it is.
  const index = showing.cards.get(card.key) ?? (card.id === null ? -1 : view.proposal.audiences.findIndex((a) => a.id === card.id));
  const proposed = index >= 0 ? view.proposal.audiences[index]?.fields[field.key] : null;
  const baseline = card.id === null ? null : (view.baseline.audiences.find((a) => a.id === card.id)?.[field.key] ?? null);
  return createElement(TextChanges, {
    id,
    label: def.label,
    text: textOf(f.value),
    multiline: def.kind === 'textarea',
    describedBy: brandFieldDescribedBy(id, {
      hint: def.hint,
      sources: f.sources,
      aiSuggested: f.aiSuggested,
      error: textLimitError(textOf(f.value), def.maxLength),
    }),
    changes: textChangesToShow({ saved: f.saved, value: f.value, proposed, baseline }),
  });
}
